//import './_pathfinder.debug'; 

/**
 * Course pathfinder module
 * Helps users discover career paths based on their skills and interests
 * @module CoursePathfinder
 */
(function () {
    'use strict';

    // ── Debug plugin ─────────────────────────────────────────────────────────────
    // Replace no-ops by importing pathfinder.debug.js before this file.
    const Debug = {
        checkUrlParam:          () => {},
        appendUrlParams:        () => {},
        addMasterSlider:        () => {},
        setupSliderAutoRefresh: () => {},
        buildCardPanel:         () => '',
        decorateOccupation:     () => {},
    };
    Object.assign(Debug, window._PathfinderDebugPlugin || {});

    // ============================================================================
    // Configuration
    // ============================================================================

    /**
     * Quiz default configuration
     */
    const CONFIG = {
        debug: false,
        scoring: {
            method: 'classic',
            useWeighting: true,
            weights: {
                4: 1.2,
                3: 1.1,
                2: 1.0,
                1: 1.1,
                0: 1.2
            },
            sliderRanges: {
                0: { min: 0, max: 2 },
                1: { min: 1.5, max: 3 },
                2: { min: 4, max: 7 },
                3: { min: 7, max: 9 },
                4: { min: 9, max: 10 }
            },
            // Default interest-based scoring adjustments (used when competency-specific adjustments not provided)
            defaultInterestAdjustments: {
                4: -0.5,  // Strong like: reward (reduce score)
                3: -0.25, // Like: small reward
                2: 0,     // Neutral: no adjustment
                1: 0.25,  // Dislike: penalty (increase score)
                0: 0.5   // Strong dislike: strong penalty
            }
        },
        pagination: {
            resultsPerPage: 12,
            currentPage: 1
        },
        fuzzyCutoff: {
            enabled: true,
            scoreTolerance: 0.1, // How close scores need to be to be considered "same group"
            maxScoreDifferenceFromFirst: 2.0 // Maximum score difference from first result to include
        },
        componentMetadata: {
            apiUrl: '',
            courseSearchUrl: window.NSWP?.urls?.courseSearch || '',
            occupationRouteBaseUrl: window.NSWP?.urls?.occupationRouteBase || '',
            maxResults: 24,
            maxTags: 10,
            courseSearchLinkText: 'See related courses',
            showMoreLinkText: 'Show {count} more result{plural}',
            requiredTagsCaption: 'This job could suit you if you enjoy:',
            matchingTagsCaption: 'This job could suit you because you enjoy:',
            contrastingTagsCaption: 'This job could challenge you in these areas:',
            noCourseRequiredText: 'You may not need a qualification for this job.',
            universityCourseSearchUrl: 'https://www.qtac.edu.au/course-search/',
            universityQualificationText: 'You may need a university qualification for this job.',
            universityQualificationLinkText: 'Search for university courses with QTAC'
        },
        occupationTypes: {
            '1': 'Managers',
            '2': 'Professionals',
            '3': 'Technicians and Trades Workers',
            '4': 'Community and Personal Service Workers',
            '5': 'Clerical and Administrative Workers',
            '6': 'Sales Workers',
            '7': 'Machinery Operators and Drivers',
            '8': 'Labourers'
        },
        aqfLevels: {
            1: 'Certificate I',
            2: 'Certificate II',
            3: 'Certificate III',
            4: 'Certificate IV',
            5: 'Diploma',
            6: 'Advanced Diploma/Associate Degree',
            7: 'Bachelor Degree',
            8: 'Bachelor Honours/Graduate Certificate/Graduate Diploma',
            9: 'Master\'s Degree',
            10: 'Doctoral Degree',
            default: 'No specific level required'
        },
        anzscoSkillLevels: {
            1: 'Bachelor degree or higher',
            2: 'Diploma, Advanced Diploma or Associate Degree',
            3: 'Certificate III or IV',
            4: 'Certificate II or III',
            5: 'Certificate I or secondary education',
            default: 'No specific level required'
        },
        interests: {
            '1': {
                code: '1',
                name: 'Working with my hands',
                enabled: true,
                competencies: [
                    { name: 'Working with my hands', adjustments: null }
                ]
            },
            '2': {
                code: '2',
                name: 'Solving problems',
                enabled: false,
                competencies: [
                    { name: 'Problem solving', adjustments: null }
                ]
            },
            '3': {
                code: '3',
                name: 'Being creative',
                enabled: true,
                competencies: [
                    { name: 'Being creative', adjustments: null }
                ]
            },
            '4': {
                code: '4',
                name: 'Working with data and details',
                enabled: false,
                competencies: [
                    { name: 'Numeracy', adjustments: null },
                    { name: 'Reading', adjustments: null }
                ]
            },
            '5': {
                code: '5',
                name: 'Leading and guiding',
                enabled: false,
                competencies: [
                    { name: 'Initiative and innovation', adjustments: null },
                    { name: 'Planning and organising', adjustments: null }
                ]
            },
            '6': {
                code: '6',
                name: 'Working with people',
                enabled: false,
                competencies: [
                    { name: 'Oral communication', adjustments: null },
                    { name: 'Teamwork', adjustments: null }
                ]
            },
            '7': {
                code: '7',
                name: 'Working outdoors',
                enabled: true,
                competencies: [
                    { name: 'Working outdoors', adjustments: null }
                ]
            }
        },
        sliderIcons: [
            { value: 0, icon: 'fa-face-confounded', class: 'level-0' },
            { value: 1, icon: 'fa-face-frown-slight', class: 'level-1' },
            { value: 2, icon: 'fa-face-meh', class: 'level-2' },
            { value: 3, icon: 'fa-face-smile', class: 'level-3' },
            { value: 4, icon: 'fa-face-laugh-beam', class: 'level-4' }
        ],
        sliderLabels: {
            0: 'Very low',
            1: 'Low',
            2: 'Neutral',
            3: 'High',
            4: 'Very high'
        },
        futureOutlookRank: {
            0: 'Very strong',
            1: 'Strong',
            2: 'Stable',
            3: 'Moderate',
            4: 'Decline'
        }
    };

    // ============================================================================
    // State Manager
    // ============================================================================

    const StateManager = {
        occupationsData: [],
        allMatchedOccupations: [],
        currentPage: 1,
        dataLoaded: false,
        domElements: {
            pathfinderElement: null,
            slidersContainer: null,
            quizAnswersSection: null,
            quizAnswersCards: null,
            noResultsMessage: null,
            showMoreContainer: null,
            showMoreButton: null
        },

        /**
         * Get element within the sliders container or from document
         * @param {string} selector - CSS selector
         * @param {boolean} useDocument - If true, search from document instead of container
         * @returns {HTMLElement|null}
         */
        getElement(selector, useDocument = false) {
            if (useDocument || !this.domElements.slidersContainer) {
                return document.querySelector(selector);
            }
            return this.domElements.slidersContainer.querySelector(selector);
        },

        /**
         * Get all elements within the sliders container or from document
         * @param {string} selector - CSS selector
         * @param {boolean} useDocument - If true, search from document instead of container
         * @returns {NodeList}
         */
        getElements(selector, useDocument = false) {
            if (useDocument || !this.domElements.slidersContainer) {
                return document.querySelectorAll(selector);
            }
            return this.domElements.slidersContainer.querySelectorAll(selector);
        },

        /**
         * Initialize DOM element references
         */
        initializeDomReferences() {
            this.domElements.pathfinderElement = document.querySelector('.qld__pathfinder');
            this.domElements.slidersContainer = document.querySelector('[data-role="pathfinder-sliders"]');
            this.domElements.quizAnswersSection = document.querySelector('[data-role="pathfinder-results"]');
            this.domElements.quizAnswersCards = document.querySelector('[data-role="pathfinder-results-cards"]');
            this.domElements.noResultsMessage = document.querySelector('[data-role="pathfinder-error-message"]');
            this.domElements.showMoreContainer = document.querySelector('[data-role="pathfinder-show-more-container"]');
            this.domElements.showMoreButton = document.querySelector('[data-role="pathfinder-show-more"]');

            if (this.domElements.quizAnswersSection) {
                this.domElements.quizAnswersSection.classList.add('hidden');
                this.domElements.quizAnswersSection.setAttribute('aria-hidden', 'true');
            }
        },

        /**
         * Add debug master slider to control all sliders at once
         */
        addDebugMasterSlider() {
            Debug.addMasterSlider(this.domElements.slidersContainer);
        },

        /**
         * Load configuration overrides from HTML data attributes
         */
        loadConfigFromHtml() {
            const { pathfinderElement } = this.domElements;
            if (!pathfinderElement) return;

            Debug.checkUrlParam(CONFIG);

            // Override API URL if specified
            if (pathfinderElement.dataset.apiUrl) {
                CONFIG.componentMetadata.apiUrl = pathfinderElement.dataset.apiUrl;
            }

            // Override max results if specified
            if (pathfinderElement.dataset.maxResults) {
                const maxResults = parseInt(pathfinderElement.dataset.maxResults);
                if (!isNaN(maxResults) && maxResults > 0) {
                    CONFIG.componentMetadata.maxResults = maxResults;
                }
            }

            // Override max tags if specified
            if (pathfinderElement.dataset.maxTags) {
                const maxTags = parseInt(pathfinderElement.dataset.maxTags);
                if (!isNaN(maxTags) && maxTags > 0) {
                    CONFIG.componentMetadata.maxTags = maxTags;
                }
            }

            // Override results per page if specified
            if (pathfinderElement.dataset.resultsPerPage) {
                const resultsPerPage = parseInt(pathfinderElement.dataset.resultsPerPage);
                if (!isNaN(resultsPerPage) && resultsPerPage > 0) {
                    CONFIG.pagination.resultsPerPage = resultsPerPage;
                }
            }

            // Override course search URL if specified
            if (pathfinderElement.dataset.courseSearchUrl) {
                CONFIG.componentMetadata.courseSearchUrl = pathfinderElement.dataset.courseSearchUrl;
            }

            // Override occupation search URL if specified
            if (pathfinderElement.dataset.occupationRouteBaseUrl) {
                CONFIG.componentMetadata.occupationRouteBaseUrl = pathfinderElement.dataset.occupationRouteBaseUrl;
            }

            // Override weighting behavior if specified
            if (pathfinderElement.dataset.useWeighting !== undefined) {
                CONFIG.scoring.useWeighting = pathfinderElement.dataset.useWeighting === 'true';
            }

            // Override link texts if specified
            if (pathfinderElement.dataset.courseSearchLinkText) {
                CONFIG.componentMetadata.courseSearchLinkText = pathfinderElement.dataset.courseSearchLinkText;
            }

            if (pathfinderElement.dataset.showMoreLinkText) {
                CONFIG.componentMetadata.showMoreLinkText = pathfinderElement.dataset.showMoreLinkText;
            }

            // Override tag captions if specified
            if (pathfinderElement.dataset.requiredTagsCaption) {
                CONFIG.componentMetadata.requiredTagsCaption = pathfinderElement.dataset.requiredTagsCaption;
            }

            if (pathfinderElement.dataset.matchingTagsCaption) {
                CONFIG.componentMetadata.matchingTagsCaption = pathfinderElement.dataset.matchingTagsCaption;
            }

            if (pathfinderElement.dataset.contrastingTagsCaption) {
                CONFIG.componentMetadata.contrastingTagsCaption = pathfinderElement.dataset.contrastingTagsCaption;
            }

            // Override course requirement messages if specified
            if (pathfinderElement.dataset.noCourseRequiredText) {
                CONFIG.componentMetadata.noCourseRequiredText = pathfinderElement.dataset.noCourseRequiredText;
            }

            if (pathfinderElement.dataset.universityCourseSearchUrl) {
                CONFIG.componentMetadata.universityCourseSearchUrl = pathfinderElement.dataset.universityCourseSearchUrl;
            }

            if (pathfinderElement.dataset.universityQualificationText) {
                CONFIG.componentMetadata.universityQualificationText = pathfinderElement.dataset.universityQualificationText;
            }

            if (pathfinderElement.dataset.universityQualificationLinkText) {
                CONFIG.componentMetadata.universityQualificationLinkText = pathfinderElement.dataset.universityQualificationLinkText;
            }

            Utils.log('Configuration loaded:', CONFIG);
        }
    };


    // ============================================================================
    // Utility Helpers
    // ============================================================================

    const Utils = {
        /**
         * Debounce function to limit how often a function can be called
         * @param {Function} func - Function to debounce
         * @param {number} wait - Wait time in milliseconds
         * @returns {Function}
         */
        debounce(func, wait = 300) {
            let timeout;
            return function executedFunction(...args) {
                const later = () => {
                    clearTimeout(timeout);
                    func(...args);
                };
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            };
        },

        /**
         * Logs messages to the console if debugging is enabled.
         * @param {...any} args - Values to log.
         * @returns {void}
         */
        log(...args) {
            if (CONFIG.debug) console.log(...args);
        },


        /**
         * Escape HTML to prevent XSS vulnerabilities
         * @param {string} text - Text to escape
         * @returns {string}
         */
        escapeHtml(text) {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
        },

        /**
         * Build URL query string from slider values (only non-neutral values)
         * @param {Array} sliderValues - Array of slider value objects
         * @returns {string} Query string (without leading ?)
         */
        buildQueryStringFromSliders(sliderValues) {
            const params = [];
            const sliders = StateManager.getElements('input[type="range"]');

            Debug.appendUrlParams(params);

            Array.from(sliders).forEach(slider => {
                const value = parseInt(slider.value);
                // Only include non-neutral values (not 2)
                if (value !== 2) {
                    const name = slider.getAttribute('name');
                    params.push(`${encodeURIComponent(name)}=${value}`);
                }
            });
            return params.join('&');
        },

        /**
         * Update browser URL with slider values without reloading the page
         * @param {Array} sliderValues - Array of slider value objects
         */
        updateUrlWithSliders(sliderValues) {
            const queryString = this.buildQueryStringFromSliders(sliderValues);
            const newUrl = queryString 
                ? `${window.location.pathname}?${queryString}`
                : window.location.pathname;
            
            const currentUrl = window.location.pathname + window.location.search;
            if (newUrl !== currentUrl) {
                window.history.pushState({}, '', newUrl);
                Utils.log('URL updated with slider values:', queryString);
            } else {
                Utils.log('URL unchanged, skipping history push');
            }
        },

        /**
         * Load slider values from URL query string
         * @returns {Object|null} Map of input names to values, or null if no params
         */
        loadSlidersFromUrl() {
            const urlParams = new URLSearchParams(window.location.search);
            const sliderValues = {};
            let hasValues = false;

            // Get names from existing sliders only
            const sliders = StateManager.getElements('input[type="range"]');
            const validNames = new Set(
                Array.from(sliders).map(slider => slider.getAttribute('name'))
            );

            // Parse URL parameters
            urlParams.forEach((value, key) => {
                const numValue = parseInt(value);
                if (validNames.has(key) && !isNaN(numValue) && numValue >= 0 && numValue <= 4) {
                    sliderValues[key] = numValue;
                    hasValues = true;
                }
            });

            return hasValues ? sliderValues : null;
        },

        /**
         * Apply slider values from an object map
         * @param {Object} sliderValues - Map of input names to values
         */
        applySlidersFromMap(sliderValues) {
            const sliders = StateManager.getElements('input[type="range"]');
            
            sliders.forEach(slider => {
                const name = slider.getAttribute('name');
                // Reset to neutral first, then apply specific value if provided
                const value = sliderValues.hasOwnProperty(name) ? sliderValues[name] : 2;
                slider.value = value;
                const container = slider.closest('.slider-container');
                UIManager.updateSliderPosition(container, value);
            });

            Utils.log('Applied slider values from URL:', sliderValues);
        },

        /**
         * Build an occupation detail page URL from title and ANZSCO code
         * @param {string} occupationTitle - Occupation title
         * @param {string} anzsco - ANZSCO code
         * @returns {string}
         */
        buildOccupationUrl(occupationTitle, anzsco) {
            const base = (CONFIG.componentMetadata.occupationRouteBaseUrl || '').replace(/\/*$/, '/');
            if (!base) return '';
            const slug = occupationTitle
                .toLowerCase()
                .replace(/[^a-z0-9\s-]/g, '')
                .trim()
                .replace(/\s+/g, '-');
            return `${base}${slug}-${anzsco}`;
        },

        /**
         * Build a job search URL from job title
         * @param {string} jobTitle - Job title to search for
         * @returns {string}
         */
        buildJobSearchUrl(jobTitle) {
            if (!jobTitle) return CONFIG.componentMetadata.courseSearchUrl;
            const encodedTitle = encodeURIComponent(jobTitle);
            const baseUrl = CONFIG.componentMetadata.courseSearchUrl || window.NSWP?.urls?.courseSearch || '';
            return `${baseUrl}?query=&f.prospects%7CrelatedJobTitles=${encodedTitle}`;
        },

        /**
         * Check if an occupation is university level
         * @param {Object} occupation - Mapped occupation object
         * @returns {boolean}
         */
        isUniversityLevel(occupation) {
            if (!occupation) return false;
            
            // Check ANZSCO Skill Level (1 = Bachelor degree or higher)
            if (occupation.anzscoSkillLevel === 1) {
                return true;
            }
            
            return false;
        },

        /**
         * Display error message to user
         * @param {string} message - Error message to display
         */
        showError(message) {
            const { noResultsMessage } = StateManager.domElements;
            if (noResultsMessage) {
                noResultsMessage.textContent = message;
                noResultsMessage.classList.remove('hidden');
                noResultsMessage.setAttribute('aria-hidden', 'false');
            }
            console.error(message);
        },

        /**
         * Hide error message
         */
        hideError() {
            const { noResultsMessage } = StateManager.domElements;
            if (noResultsMessage) {
                noResultsMessage.classList.add('hidden');
                noResultsMessage.setAttribute('aria-hidden', 'true');
            }
        }
    };

    // ============================================================================
    // Data Manager
    // ============================================================================

    const DataManager = {
        /**
         * Load occupations data from API
         * @returns {Promise}
         */
        async loadOccupationsData() {
            try {
                const response = await fetch(CONFIG.componentMetadata.apiUrl);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const json = await response.json();
                const rawOccupations = Array.isArray(json) ? json : [json];

                // Drop occupations missing competency scores needed for scoring
                StateManager.occupationsData = rawOccupations.filter(occ =>
                    ScoringManager.hasCompetencies(occ)
                );
                StateManager.dataLoaded = true;

                Utils.log(`Loaded ${StateManager.occupationsData.length} occupations (of ${rawOccupations.length} total)`);
                return StateManager.occupationsData;
            } catch (error) {
                console.error('Error fetching occupations data:', error);
                Utils.showError('Sorry, we could not load the occupations data. Please try again later.');
                throw error;
            }
        },

        /**
         * Map raw occupation data to standardized format
         * @param {Object} rawOccupation - Raw occupation data from API
         * @returns {Object}
         */
        mapOccupationData(rawOccupation) {
            const toNum = v => (v === null || v === undefined || v === '' ? 0 : Number(v));
            const mapped = {
                occupationTitle: rawOccupation.title || rawOccupation.occupationTitle || '',
                anzsco: rawOccupation.anzsco || rawOccupation.occupation_ANZSCO_code || '',
                description: rawOccupation.description || rawOccupation.occupationDescription || '',
                anzscoSkillLevel: toNum(rawOccupation.anzscoSkillLevel),
                pageUrl: rawOccupation.pageurl || rawOccupation.occupation_pageUrl || '',
                occupationTypeCode: rawOccupation.typecode || rawOccupation.occupationTypeCode || '',
                courseCodes: Array.isArray(rawOccupation.courseCodes) ? rawOccupation.courseCodes : [],
                competencies: {}
            };

            // Map competency ratings from coreCompetencies array
            if (Array.isArray(rawOccupation.coreCompetencies)) {
                rawOccupation.coreCompetencies.forEach(({ competency: competencyName, score: rating }) => {
                    mapped.competencies[competencyName] = rating || 0;
                });
            }

            return mapped;
        }
    };

    // ============================================================================
    // Scoring Manager
    // ============================================================================

    const ScoringManager = {
        /**
         * Get the range values for a slider position
         * @param {string|number} sliderValue - Slider value (0-4)
         * @returns {Object}
         */
        getSliderRange(sliderValue) {
            const value = String(sliderValue);
            return CONFIG.scoring.sliderRanges[value] || { min: 0, max: 0 };
        },

        /**
         * Calculate the midpoint of a slider range
         * @param {string|number} sliderValue - Slider value (0-4)
         * @returns {number}
         */
        getSliderMidpoint(sliderValue) {
            const range = this.getSliderRange(sliderValue);
            return (range.min + range.max) / 2;

        },

        /**
         * Get weight for a slider value based on scoring configuration
         * @param {number} sliderValue - Slider value (0-4)
         * @returns {number}
         */
        getSliderWeight(sliderValue) {
            if (!CONFIG.scoring.useWeighting) return 1.0;
            return CONFIG.scoring.weights[sliderValue] || 1.0;
        },

        /**
         * Scale slider value (0-4) to job rating scale (0-10)
         * @param {number} sliderValue - Slider value (0-4)
         * @returns {number} Scaled value (2-10)
         */
        scaleSliderValue(sliderValue) {
            return (sliderValue + 1) * 2;
        },

        /**
         * Get all current slider values
         * @returns {Array}
         */
        getSliderValues() {
            const sliders = StateManager.getElements('input[type="range"]');
            return Array.from(sliders).map(slider => ({
                value: parseInt(slider.value),
                displayName: slider.dataset.competency,
                element: slider
            }));
        },


        /**
         * True if the occupation has usable competency score data
         * @param {Object} occupation - Raw occupation data
         * @returns {boolean}
         */
        hasCompetencies(occupation) {
            return Array.isArray(occupation.coreCompetencies) && occupation.coreCompetencies.length > 0;
        },

        /**
         * Calculate match score between user preferences and occupation
         * Includes interest-based adjustments after initial scoring
         * @param {Object} occupation - Raw occupation data
         * @param {Array} sliderValues - Current slider values
         * @returns {Object} Object with baseScore and finalScore
         */
        calculateMatchScore(occupation, sliderValues) {
            const baseScore = this.calculateClassicScore(occupation, sliderValues);
            const finalScore = this.applyInterestAdjustmentsToScore(occupation, baseScore, sliderValues);
            return { baseScore, finalScore };
        },

        /**
         * Classic scoring method - calculates distance using linear scaling
         * Slider values are scaled to match job rating scale: (N + 1) * 2
         * @param {Object} occupation - Raw occupation data
         * @param {Array} sliderValues - Current slider values
         * @returns {number}
         */
        calculateClassicScore(occupation, sliderValues) {
            let totalWeightedDifference = 0;
            let totalWeight = 0;

            sliderValues.forEach(({ value, displayName }) => {
                //const targetValue = this.scaleSliderValue(value);
                const targetValue = this.getSliderMidpoint(value);
                
                const coreComp = occupation.coreCompetencies?.find(c => c.competency === displayName);

                if (coreComp) {
                    const jobValue = parseFloat(coreComp.score);

                    if (!isNaN(jobValue)) {
                        const difference = Math.abs(jobValue - targetValue);
                        const weight = this.getSliderWeight(value);

                        totalWeightedDifference += difference * weight;
                        totalWeight += weight;
                    }
                }
            });

            return totalWeight > 0 ? totalWeightedDifference / totalWeight : Number.MAX_VALUE;
        },

        /**
         * Generate matching, contrasting, and required competency tags for an occupation
         * Tags are generated by sorting competencies by job rating (descending) and placing them into buckets
         * @param {Object} mappedOccupation - Mapped occupation data
         * @param {Array} sliderValues - Current slider values
         * @returns {Object} {matchingTags: Array, contrastingTags: Array, requiredTags: Array}
         */
        generateTags(mappedOccupation, sliderValues) {
            // Create a map of slider values for quick lookup
            const sliderMap = {};
            sliderValues.forEach(({ value, displayName }) => {
                sliderMap[displayName] = value;
            });

            // Get all competencies with their ratings and sort by rating (descending)
            const sortedCompetencies = Object.entries(mappedOccupation.competencies)
                .map(([competency, rating]) => ({
                    competency,
                    rating: parseFloat(rating),
                    sliderValue: sliderMap[competency] !== undefined ? sliderMap[competency] : 2
                }))
                .filter(item => !isNaN(item.rating))
                .sort((a, b) => b.rating - a.rating);

            // Initialize buckets
            const matchingTags = [];
            const contrastingTags = [];
            const requiredTags = [];

            // Loop through sorted competencies and place into buckets
            for (const { competency, rating, sliderValue } of sortedCompetencies) {
                // Matching bucket: slider > 2 AND rating >= 5
                if (sliderValue > 2 && rating >= 5 && matchingTags.length < CONFIG.componentMetadata.maxTags) {
                    matchingTags.push(competency);
                }

                // Contrasting bucket: slider 0-1 AND rating >= 5
                else if (sliderValue <= 1 && rating >= 5 && contrastingTags.length < CONFIG.componentMetadata.maxTags) {
                    contrastingTags.push(competency);
                }

                // Required bucket: rating >= 5 (collect all initially)
                else if (rating >= 5) {
                    requiredTags.push({ competency, rating });
                }
            }

            // Apply smart filtering to required tags based on rating distribution
            let filteredRequired = [];

            if (requiredTags.length > 0) {
                const ratings = requiredTags.map(item => item.rating);
                const maxRating = Math.max(...ratings);
                const minRating = Math.min(...ratings);
                const ratingRange = maxRating - minRating;

                let requiredThreshold = 5;

                if (ratingRange >= 2) {
                    // Get unique ratings and count items at each level
                    const uniqueRatings = [...new Set(ratings)].sort((a, b) => b - a);
                    const topRatingCount = requiredTags.filter(item => item.rating === uniqueRatings[0]).length;

                    // If top rating has very few items (1-2) and there's a second level, include second level
                    if (topRatingCount <= 2 && uniqueRatings.length > 1) {
                        requiredThreshold = uniqueRatings[1];
                    } else if (uniqueRatings.length >= 2) {
                        // Otherwise, show top 2 levels
                        requiredThreshold = uniqueRatings[1];
                    } else {
                        requiredThreshold = uniqueRatings[0];
                    }
                } else if (ratingRange === 1) {
                    // Small range, show both levels
                    requiredThreshold = minRating;
                } else {
                    // All same rating, use that rating
                    requiredThreshold = maxRating;
                }

                Utils.log(`Required tags threshold: ${requiredThreshold} (range: ${minRating}-${maxRating})`);

                // Filter required tags to only those meeting threshold, then extract just the competency names
                filteredRequired = requiredTags
                    .filter(item => item.rating >= requiredThreshold)
                    .slice(0, CONFIG.componentMetadata.maxTags)
                    .map(item => item.competency);
            }

            return { matchingTags, contrastingTags, requiredTags: filteredRequired };
        },

        /**
         * Find fuzzy cutoff position that includes all jobs with similar scores
         * @param {Array} scoredOccupations - Sorted array of scored occupations (ascending)
         * @param {number} maxResults - Target maximum number of results
         * @returns {number} - Actual cutoff position
         */
        findFuzzyCutoff(scoredOccupations, maxResults) {
            if (!CONFIG.fuzzyCutoff.enabled || scoredOccupations.length <= maxResults) {
                return Math.min(scoredOccupations.length, maxResults);
            }

            const firstScore = scoredOccupations[0].score;
            const cutoffScore = scoredOccupations[maxResults - 1].score;
            const scoreTolerance = CONFIG.fuzzyCutoff.scoreTolerance;
            const maxScoreDiff = CONFIG.fuzzyCutoff.maxScoreDifferenceFromFirst;

            // If the cutoff score is too far from the first result, find a better breakpoint
            if (cutoffScore - firstScore > maxScoreDiff) {
                // Work backwards to find the last result within acceptable range
                for (let i = maxResults - 1; i >= 0; i--) {
                    if (scoredOccupations[i].score - firstScore <= maxScoreDiff) {
                        // Found acceptable position, now include all with similar score
                        const acceptableScore = scoredOccupations[i].score;
                        let cutoffPosition = i + 1;

                        // Include all results with similar scores
                        while (cutoffPosition < scoredOccupations.length &&
                            Math.abs(scoredOccupations[cutoffPosition].score - acceptableScore) <= scoreTolerance) {
                            cutoffPosition++;
                        }

                        return cutoffPosition;
                    }
                }
                // If nothing found within range, return minimum viable set
                return Math.min(5, scoredOccupations.length);
            }

            // Include all results with scores similar to the cutoff score
            let cutoffPosition = maxResults;
            while (cutoffPosition < scoredOccupations.length &&
                Math.abs(scoredOccupations[cutoffPosition].score - cutoffScore) <= scoreTolerance) {
                cutoffPosition++;
            }

            // Double-check we're not too far from first result
            if (cutoffPosition > maxResults &&
                scoredOccupations[cutoffPosition - 1].score - firstScore > maxScoreDiff) {
                // Walk back to last good position
                while (cutoffPosition > 0 &&
                    scoredOccupations[cutoffPosition - 1].score - firstScore > maxScoreDiff) {
                    cutoffPosition--;
                }
            }

            return Math.max(cutoffPosition, 1); // At least return 1 result
        },

        /**
         * Find and score all matching occupations
         * @returns {Array}
         */
        findMatchingOccupations() {
            if (!StateManager.occupationsData.length) {
                Utils.showError('Sorry, no occupations data is available. Please refresh the page.');
                return [];
            }

            const sliderValues = this.getSliderValues();

            // Score all occupations (including interest adjustments)
            const scoredOccupations = StateManager.occupationsData.map(occupation => {
                const scoreResult = this.calculateMatchScore(occupation, sliderValues);
                return {
                    occupation,
                    score: scoreResult.finalScore,
                    baseScore: scoreResult.baseScore
                };
            });

            // Sort by score (ascending - lower is better), with futureOutlook as secondary sort (descending strength)
            const FUTURE_OUTLOOK_FALLBACK_RANK = Object.keys(CONFIG.futureOutlookRank).length;
            const futureOutlookRankLookup = Object.fromEntries(
                Object.entries(CONFIG.futureOutlookRank).map(([rank, label]) => [label, Number(rank)])
            );
            const getFutureOutlookRank = (occ) => {
                const outlook = occ.occupation?.statistics?.futureOutlook;
                return futureOutlookRankLookup[outlook] ?? FUTURE_OUTLOOK_FALLBACK_RANK;
            };
            scoredOccupations.sort((a, b) => {
                const scoreDiff = a.score - b.score;
                if (scoreDiff !== 0) return scoreDiff;
                return getFutureOutlookRank(a) - getFutureOutlookRank(b);
            });

            // Find fuzzy cutoff position
            const cutoffPosition = this.findFuzzyCutoff(scoredOccupations, CONFIG.componentMetadata.maxResults);

            Utils.log(`Fuzzy cutoff: ${cutoffPosition} results (target was ${CONFIG.componentMetadata.maxResults})`);
            Utils.log(`Score range: ${scoredOccupations[0].score.toFixed(3)} to ${scoredOccupations[cutoffPosition - 1].score.toFixed(3)}`);
        

            return scoredOccupations.slice(0, cutoffPosition);
        },

        /**
         * Apply interest-based score adjustments to a single occupation score
         * This runs after initial scoring to penalize/reward based on occupation interests
         * @param {Object} occupation - Raw occupation data
         * @param {number} baseScore - The base score before interest adjustments
         * @param {Array} sliderValues - Current slider values
         * @returns {number} - Adjusted score
         */
        applyInterestAdjustmentsToScore(occupation, baseScore, sliderValues) {
            // Check if occupation has interests data
            if (!occupation.interests || !Array.isArray(occupation.interests)) {
                return baseScore; // Return unchanged if no interests data
            }
            
            // Build a map of competency names to slider values for quick lookup
            const competencySliderMap = {};
            sliderValues.forEach(({ value, displayName }) => {
                competencySliderMap[displayName] = value;
            });

            let totalAdjustment = 0;
            let adjustmentCount = 0;

            // Process each interest in the occupation
            occupation.interests.forEach(interest => {
                const interestCode = interest.code;
                const interestConfig = CONFIG.interests[interestCode];

                // Skip if interest is not configured or not enabled
                if (!interestConfig || !interestConfig.enabled) {
                    return;
                }

                // Get competencies that this interest maps to (now array of objects)
                const mappedCompetencies = interestConfig.competencies || [{ name: interestConfig.name, adjustments: null }];
                
                // Check each mapped competency for a slider value
                mappedCompetencies.forEach(competencyConfig => {
                    const competencyName = competencyConfig.name;
                    const sliderValue = competencySliderMap[competencyName];

                    // Skip if no slider exists for this competency
                    if (sliderValue === undefined) {
                        return;
                    }

                    // Use competency-specific adjustments if provided, otherwise use default
                    const adjustmentsMap = competencyConfig.adjustments || CONFIG.scoring.defaultInterestAdjustments;
                    const adjustmentMultiplier = adjustmentsMap[sliderValue];
                    
                    // Skip if no adjustment defined for this slider value
                    if (adjustmentMultiplier === undefined) {
                        return;
                    }

                    // Calculate adjustment: base score * multiplier
                    // Positive multiplier = penalty (increases score = worse match)
                    // Negative multiplier = reward (decreases score = better match)
                    // Zero = no adjustment
                    const adjustment = baseScore * adjustmentMultiplier;

                    Utils.log(`Interest adjustment for ${occupation.occupationTitle}: ${interestConfig.name} via "${competencyName}" (slider: ${sliderValue}, multiplier: ${adjustmentMultiplier}) = ${adjustment.toFixed(3)}`);

                    totalAdjustment += adjustment;
                    adjustmentCount++;
                });
            });

            // Apply the total adjustment to the score
            if (adjustmentCount > 0) {
                const adjustedScore = baseScore + totalAdjustment;
                Utils.log(`Total interest adjustment for ${occupation.occupationTitle}: ${totalAdjustment.toFixed(3)} (${baseScore.toFixed(3)} -> ${adjustedScore.toFixed(3)})`);
                return adjustedScore;
            }

            return baseScore;
        }
    };

    // ============================================================================
    // DOM Builder
    // ============================================================================

    const DOMBuilder = {
        /**
         * Create a DOM element with attributes
         * @param {string} tag - HTML tag name
         * @param {Object} attributes - Element attributes
         * @param {string|Array} children - Child elements or text content
         * @returns {HTMLElement}
         */
        createElement(tag, attributes = {}, children = null) {
            const element = document.createElement(tag);

            Object.entries(attributes).forEach(([key, value]) => {
                if (key === 'className') {
                    element.className = value;
                } else if (key === 'dataset') {
                    Object.entries(value).forEach(([dataKey, dataValue]) => {
                        element.dataset[dataKey] = dataValue;
                    });
                } else {
                    element.setAttribute(key, value);
                }
            });

            if (children) {
                if (typeof children === 'string') {
                    element.textContent = children;
                } else if (Array.isArray(children)) {
                    children.forEach(child => {
                        if (child instanceof HTMLElement) {
                            element.appendChild(child);
                        }
                    });
                } else if (children instanceof HTMLElement) {
                    element.appendChild(children);
                }
            }

            return element;
        },

        /**
         * Generate tag list group HTML
         * @param {Array} tags - Array of tag strings
         * @param {string} caption - Caption text for the tag group
         * @param {string} tagClass - CSS class for the tag style
         * @returns {string} HTML string for the tag group or empty string if no tags
         */
        createTagListGroup(tags, caption, tagClass) {
            if (!tags || tags.length === 0) return '';
            
            return `
                <figure class="qld__tag-list-group">
                    ${caption ? `<figcaption class="qld__default-xs">${caption}</figcaption>` : ''}
                    <ul class="qld__tag-list qld__margin-t-none">
                        ${tags.map(tag => `<li class="qld__tag ${tagClass}">${Utils.escapeHtml(tag)}</li>`).join('')}
                    </ul>
                </figure>`;
        },

        /**
         * Create occupation card element using template literals
         * @param {Object} mappedOccupation - Mapped occupation data
         * @param {Array} sliderValues - Current slider values
         * @returns {HTMLElement}
         */
        createOccupationCard(mappedOccupation, sliderValues) {
            const courseSearchLinkText = CONFIG.componentMetadata.courseSearchLinkText;
                        
            // Get URLs
            const courseSearchUrl = Utils.buildJobSearchUrl(mappedOccupation.occupationTitle);
            
            // Get tags
            const { matchingTags, contrastingTags, requiredTags } = ScoringManager.generateTags(mappedOccupation, sliderValues);

            const matchingTagsHtml = matchingTags.length > 0 
            ? this.createTagListGroup(matchingTags, CONFIG.componentMetadata.matchingTagsCaption, 'qld__tag--course-info') // aligned tags
            : this.createTagListGroup(requiredTags, CONFIG.componentMetadata.requiredTagsCaption, 'qld__tag--course-info'); // required tags

            const contrastingTagsHtml = this.createTagListGroup(contrastingTags, CONFIG.componentMetadata.contrastingTagsCaption, 'qld__tag--info');

            const debugHtml = Debug.buildCardPanel(mappedOccupation, sliderValues, CONFIG, StateManager, ScoringManager, Utils);

            // footer link
            let footerLinkHtml = '';
            if (mappedOccupation.courseCodes.length > 0) {
                footerLinkHtml = `<a href="${Utils.escapeHtml(courseSearchUrl)}" class="qld__direction-link qld__direction-link--right" aria-label="${Utils.escapeHtml(courseSearchLinkText)} - ${Utils.escapeHtml(mappedOccupation.occupationTitle)}">${Utils.escapeHtml(courseSearchLinkText)}</a>`;
            } else if(Utils.isUniversityLevel(mappedOccupation)) {
                footerLinkHtml = `${CONFIG.componentMetadata.universityQualificationText} <a href="${CONFIG.componentMetadata.universityCourseSearchUrl}">${CONFIG.componentMetadata.universityQualificationLinkText}</a>`;
            } else {
                footerLinkHtml = CONFIG.componentMetadata.noCourseRequiredText;
            }

            
            // Build the HTML
            const html = `
                <div class="qld__card">
                    <div class="qld__card__inner">
                        <div class="qld__card__content">
                            <div class="qld__card__content-inner">
                                <h3 class="qld__card__title">${(() => { const url = Utils.buildOccupationUrl(mappedOccupation.occupationTitle, mappedOccupation.anzsco); return url ? `<a href="${Utils.escapeHtml(url)}">${Utils.escapeHtml(mappedOccupation.occupationTitle)}</a>` : Utils.escapeHtml(mappedOccupation.occupationTitle); })()}</h3>
                                ${mappedOccupation.anzsco ? `<p class="qld__margin-t-label"><span data-role="occupation-id"><strong>ANZSCO ${Utils.escapeHtml(mappedOccupation.anzsco)}</strong></span></p>` : ''}
                                <p class="qld__card__description">${Utils.escapeHtml(mappedOccupation.description)}</p>
                                <hr class="qld__horizontal-rule" aria-hidden="true">
                                <div class="qld__card__footer-inner">
                                    ${matchingTagsHtml}
                                    ${contrastingTagsHtml}
                                </div>
                            </div>
                        </div>
                        <div class="qld__card__footer">
                            <hr class="qld__horizontal-rule" aria-hidden="true">
                            <div class="qld__card__footer-inner">
                                ${footerLinkHtml}
                            </div>
                        </div>
                        ${debugHtml}
                    </div>
                </div>
            `;
            
            // Create the list item element
            const listItem = document.createElement('li');
            listItem.className = 'col-xs-12 col-md-6';
            listItem.dataset.occupationTitle = mappedOccupation.occupationTitle || '';
            listItem.dataset.occupationType = CONFIG.occupationTypes[mappedOccupation.occupationTypeCode] || '';
            listItem.dataset.anzsco = mappedOccupation.anzsco || '';
            listItem.dataset.score = mappedOccupation.score?.toFixed(3) || '';
            listItem.innerHTML = html;
            
            return listItem;
        }
    };

    // ============================================================================
    // Pagination Manager
    // ============================================================================

    const PaginationManager = {
        /**
         * Reset pagination state
         */
        reset() {
            StateManager.currentPage = 1;
            StateManager.allMatchedOccupations = [];
        },

        /**
         * Get results for a specific page
         * @param {number} pageNumber - Page number (1-indexed)
         * @returns {Array}
         */
        getPageResults(pageNumber) {
            const startIndex = (pageNumber - 1) * CONFIG.pagination.resultsPerPage;
            const endIndex = startIndex + CONFIG.pagination.resultsPerPage;
            return StateManager.allMatchedOccupations.slice(startIndex, endIndex);
        },

        /**
         * Check if there are more results to show
         * @returns {boolean}
         */
        hasMoreResults() {
            const totalResults = StateManager.allMatchedOccupations.length;
            const shownResults = StateManager.currentPage * CONFIG.pagination.resultsPerPage;
            return shownResults < totalResults;
        },

        /**
         * Get total number of pages
         * @returns {number}
         */
        getTotalPages() {
            return Math.ceil(StateManager.allMatchedOccupations.length / CONFIG.pagination.resultsPerPage);
        },

        /**
         * Load next page of results
         */
        loadMoreResults() {
            if (!this.hasMoreResults()) {
                return;
            }

            StateManager.currentPage++;
            const nextPageResults = this.getPageResults(StateManager.currentPage);

            // Display the next page of cards
            UIManager.displayOccupationCards(nextPageResults, StateManager.currentPage);

            // Update show more button visibility
            this.updateShowMoreButton();
        },

        /**
         * Update show more button visibility and text
         */
        updateShowMoreButton() {
            const { showMoreContainer, showMoreButton } = StateManager.domElements;

            if (!showMoreContainer || !showMoreButton) {
                return;
            }

            if (this.hasMoreResults()) {
                const remainingResults = StateManager.allMatchedOccupations.length -
                    (StateManager.currentPage * CONFIG.pagination.resultsPerPage);
                const nextPageCount = Math.min(remainingResults, CONFIG.pagination.resultsPerPage);

                // Use configurable button text template
                const buttonText = CONFIG.componentMetadata.showMoreLinkText
                    .replace('{count}', nextPageCount)
                    .replace('{plural}', nextPageCount !== 1 ? 's' : '');

                showMoreButton.textContent = buttonText;
                showMoreContainer.classList.remove('hidden');
                showMoreContainer.setAttribute('aria-hidden', 'false');
            } else {
                showMoreContainer.classList.add('hidden');
                showMoreContainer.setAttribute('aria-hidden', 'true');
            }
        },

        /**
         * Initialize pagination for new results
         * @param {Array} allResults - All matched occupations
         */
        initialize(allResults) {
            StateManager.allMatchedOccupations = allResults;
            StateManager.currentPage = 1;
            this.updateShowMoreButton();
        }
    };

    // ============================================================================
    // UI Manager
    // ============================================================================

    const UIManager = {
        /**
         * Update slider visual position
         * @param {HTMLElement} container - Slider container element
         * @param {string|number} value - Slider value
         */
        updateSliderPosition(container, value) {
            // Remove all position classes
            for (let i = 0; i <= 4; i++) {
                container.classList.remove(`position-${i}`);
            }
            container.classList.add(`position-${value}`);
        },

        /**
         * Setup slider event handlers with integrated scroll detection
         */
        setupSliderInteractions() {
            const rangeInputs = StateManager.getElements('input[type="range"]');
            const moveThreshold = 8;

            rangeInputs.forEach(input => {
                const container = input.closest('.slider-container');
                const iconWrappers = container.querySelectorAll('.slider-icon-wrapper');

                // Set descriptive label for screen readers
                input.setAttribute('aria-valuetext', CONFIG.sliderLabels[input.value]);

                this.updateSliderPosition(container, input.value);

                // Disable pointer events on the range input so only icons are interactive
                input.style.pointerEvents = 'none';

                // Update position when input value changes (triggered by icon clicks)
                input.addEventListener('input', () => {
                    const value = input.value;
                    this.updateSliderPosition(container, value);
                    input.setAttribute('aria-valuetext', CONFIG.sliderLabels[value]);
                });

                Debug.setupSliderAutoRefresh(input, StateManager.domElements.quizAnswersSection, QuizController, Utils);

                iconWrappers.forEach(wrapper => {
                    let iconTouchStart = null;
                    
                    const clickHandler = () => {
                        const value = wrapper.getAttribute('data-value');
                        input.value = value;
                        input.dispatchEvent(new Event('input', { bubbles: true }));
                    };

                    wrapper.addEventListener('click', clickHandler);

                    wrapper.addEventListener('touchstart', (e) => {
                        const touch = e.touches[0];
                        iconTouchStart = {
                            startY: touch.clientY,
                            startX: touch.clientX,
                            hasScrolled: false
                        };
                    }, { passive: true });

                    wrapper.addEventListener('touchmove', (e) => {
                        if (iconTouchStart && !iconTouchStart.hasScrolled) {
                            const touch = e.touches[0];
                            const deltaY = Math.abs(touch.clientY - iconTouchStart.startY);
                            const deltaX = Math.abs(touch.clientX - iconTouchStart.startX);
                            
                            // Mark as scrolled if movement exceeds threshold
                            if (deltaY > moveThreshold || deltaX > moveThreshold) {
                                iconTouchStart.hasScrolled = true;
                            }
                        }
                    }, { passive: true });

                    wrapper.addEventListener('touchend', (e) => {
                        if (iconTouchStart) {
                            // Only trigger if user didn't scroll during the touch
                            if (!iconTouchStart.hasScrolled) {
                                e.preventDefault();
                                clickHandler();
                            }
                            
                            iconTouchStart = null;
                        }
                    });
                });
            });
        },

        /**
         * Display occupation cards in the results section
         * @param {Array} pageMatches - Array of matching occupations for this page
         * @param {number} pageNumber - Page number (1-indexed, defaults to 1)
         */
        displayOccupationCards(pageMatches, pageNumber = 1) {
            const { quizAnswersCards } = StateManager.domElements;
            if (!quizAnswersCards) {
                console.error('Quiz answers cards container not found');
                return;
            }

            // Clear all cards on first page
            if (pageNumber === 1) {
                quizAnswersCards.innerHTML = '';
            }

            const sliderValues = ScoringManager.getSliderValues();
            
            // Calculate starting position for this page
            const startPosition = (pageNumber - 1) * CONFIG.pagination.resultsPerPage;

            // Create card list for this page
            const cardList = DOMBuilder.createElement('ul', {
                className: 'qld__card-list qld__card-list--matchheight qld__pathfinder-results__list',
                dataset: {
                    page: pageNumber.toString()
                }
            }, pageMatches.map(({ occupation, score, baseScore }, index) => {
                const mapped = DataManager.mapOccupationData(occupation);
                mapped.score = score;
                mapped.baseScore = baseScore;
                Debug.decorateOccupation(mapped, occupation, startPosition + index + 1);
                return DOMBuilder.createOccupationCard(mapped, sliderValues);
            }));

            quizAnswersCards.appendChild(cardList);
        }
    };

    // ============================================================================
    // Quiz Controller
    // ============================================================================

    const QuizController = {
        /**
         * Show quiz results based on current slider values
         * @param {boolean} shouldScroll - Whether to scroll to results (defaults to false)
         * @param {boolean} updateUrl - Whether to update the browser URL (defaults to true)
         */
        showResults(shouldScroll = false, updateUrl = true) {
            const { quizAnswersSection } = StateManager.domElements;

            Utils.hideError();

            if (quizAnswersSection) {
                quizAnswersSection.classList.add('hidden');
                quizAnswersSection.setAttribute('aria-hidden', 'true');
            }

            // Reset pagination
            PaginationManager.reset();

            // Get current slider values
            const sliderValues = ScoringManager.getSliderValues();

            // Update URL with slider values (unless explicitly disabled, e.g., when loading from URL)
            if (updateUrl) {
                Utils.updateUrlWithSliders(sliderValues);
            }

            // Get all matching occupations
            const allMatches = ScoringManager.findMatchingOccupations();

            Utils.log(`Found ${allMatches.length} matching occupations`);

            if (allMatches.length > 0) {
                // Store all results and initialize pagination
                PaginationManager.initialize(allMatches);

                // Get and display first page of results
                const firstPageResults = PaginationManager.getPageResults(1);
                UIManager.displayOccupationCards(firstPageResults, 1);

                // Update show more button
                PaginationManager.updateShowMoreButton();

                if (quizAnswersSection) {
                    quizAnswersSection.classList.remove('hidden');
                    quizAnswersSection.setAttribute('aria-hidden', 'false');
                }

                // Only scroll if explicitly requested (button click)
                if (shouldScroll) {
                    setTimeout(() => {
                        if (quizAnswersSection) {
                            quizAnswersSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    }, 100);
                }
            } else {
                Utils.showError('No matching occupations found. Try adjusting your preferences.');
            }
        },

        /**
         * Reset quiz to initial state
         */
        resetQuiz() {
            const { quizAnswersSection, showMoreContainer } = StateManager.domElements;

            // Reset all sliders to neutral (2)
            const rangeInputs = StateManager.getElements('input[type="range"]');
            rangeInputs.forEach(input => {
                input.value = '2';
                const container = input.closest('.slider-container');
                UIManager.updateSliderPosition(container, '2');
            });

            // Hide results and show more button
            if (quizAnswersSection) {
                quizAnswersSection.classList.add('hidden');
                quizAnswersSection.setAttribute('aria-hidden', 'true');
            }

            if (showMoreContainer) {
                showMoreContainer.classList.add('hidden');
                showMoreContainer.setAttribute('aria-hidden', 'true');
            }

            PaginationManager.reset();

            Utils.hideError();

            // Clear URL query parameters
            window.history.pushState({}, '', window.location.pathname);

            // Scroll to top of quiz
            const quizElement = document.querySelector('.qld__pathfinder');
            if (quizElement) {
                quizElement.scrollIntoView({ behavior: 'smooth' });
            }
        },

        /**
         * Setup event listeners for quiz controls
         */
        setupEventListeners() {
            const showResultsButton = document.querySelector('[data-role="pathfinder-show-results"]');
            const resetButton = document.querySelector('[data-role="pathfinder-reset"]');
            const showMoreButton = document.querySelector('[data-role="pathfinder-show-more"]');

            if (showResultsButton) {
                showResultsButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (!StateManager.dataLoaded) {
                        return;
                    }
                    this.showResults(true);
                });
            }

            if (resetButton) {
                resetButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.resetQuiz();
                });
            }

            if (showMoreButton) {
                showMoreButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    PaginationManager.loadMoreResults();
                });
            }

            // Handle browser back/forward buttons
            window.addEventListener('popstate', () => {
                const urlSliderValues = Utils.loadSlidersFromUrl();
                
                if (urlSliderValues) {
                    Utils.applySlidersFromMap(urlSliderValues);
                    this.showResults(false, false);
                } else {
                    const rangeInputs = StateManager.getElements('input[type="range"]');
                    rangeInputs.forEach(input => {
                        input.value = '2';
                        const container = input.closest('.slider-container');
                        UIManager.updateSliderPosition(container, '2');
                    });
                    
                    // Hide results section
                    const { quizAnswersSection, showMoreContainer } = StateManager.domElements;
                    if (quizAnswersSection) {
                        quizAnswersSection.classList.add('hidden');
                        quizAnswersSection.setAttribute('aria-hidden', 'true');
                    }
                    
                    if (showMoreContainer) {
                        showMoreContainer.classList.add('hidden');
                        showMoreContainer.setAttribute('aria-hidden', 'true');
                    }
                    
                    PaginationManager.reset();
                    Utils.hideError();
                }
            });
        },

        /**
         * Initialize the quiz application
         */
        async initialize() {
            // Check if component exists on page
            if (!document.querySelector('[data-role="pathfinder"]')) {
                return;
            }

            try {
                // Initialize DOM references
                StateManager.initializeDomReferences();

                // Load configuration from HTML data attributes
                StateManager.loadConfigFromHtml();

                // Add debug master slider if DEBUG mode is enabled
                StateManager.addDebugMasterSlider();

                // Setup UI
                UIManager.setupSliderInteractions();
                this.setupEventListeners();

                const showResultsBtn = document.querySelector('[data-role="pathfinder-show-results"]');
                if (showResultsBtn) {
                    showResultsBtn.disabled = true;
                    showResultsBtn.setAttribute('aria-disabled', 'true');
                }

                // Load data
                await DataManager.loadOccupationsData();

                if (showResultsBtn) {
                    showResultsBtn.disabled = false;
                    showResultsBtn.removeAttribute('aria-disabled');
                }

                // Check for slider values in URL and auto-load results
                const urlSliderValues = Utils.loadSlidersFromUrl();
                if (urlSliderValues) {
                    Utils.log('Loading slider values from URL:', urlSliderValues);
                    Utils.applySlidersFromMap(urlSliderValues);
                    this.showResults(false, false);
                }

                Utils.log('Course pathfinder initialized successfully');
            } catch (error) {
                console.error('Error initializing quiz:', error);
                Utils.showError('We encountered an error initializing the pathfinder tool. Please try refreshing the page.');
            }
        }
    };

    // ============================================================================
    // Initialization
    // ============================================================================

    // Start when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => QuizController.initialize());
    } else {
        QuizController.initialize();
    }

})();
